import { useState } from 'react';
import { AsyncBoundary, Card, Drawer, Notice, useToast } from '@kollez/design-system';
import { useIntegrityFlags, useAppealIntegrityFlag, type IntegrityFlagRow } from '@/lib/queries';
import { formatIndianDate, formatTime } from '@kollez/shared';

/**
 * /profile/integrity — student's own IntegrityFlag inbox.
 *
 * Ship 5g surfaced integrity flagging + 7-day appeal windows via
 * POST /assessments/integrity/flags/:id/appeal, but the student side had
 * no view. This page lists the caller's flags, colored by severity, with
 * an appeal drawer for voided flags that are still inside the 7-day
 * window. Cleared / explained / retest flags render read-only.
 */

const KIND_LABEL: Record<string, string> = {
  answerMatch: 'Answer match',
  faceMismatch: 'Face mismatch',
  impossibleSpeed: 'Impossible speed',
  tabSwitch: 'Tab switch',
  paste: 'Paste',
  codeSimilarity: 'Code similarity',
  networkTiming: 'Network timing',
};

const STATUS_TONE: Record<string, string> = {
  open: 'badge-warning',
  cleared: 'badge-success',
  explained: 'badge-success',
  retest: 'badge-info',
  voided: 'badge-danger',
};

const SEVERITY_TONE: Record<string, string> = {
  low: '',
  medium: 'badge-warning',
  high: 'badge-danger',
};

function daysRemaining(iso?: string): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

export function StudentIntegrityPage() {
  const toast = useToast();
  const q = useIntegrityFlags({ mine: true, limit: 100, sortBy: 'createdAt', sortDir: 'desc' });
  const appeal = useAppealIntegrityFlag();
  const [openId, setOpenId] = useState<string | null>(null);
  const [appealText, setAppealText] = useState('');

  const rows = q.data?.rows ?? [];
  const summary = q.data?.summary;
  const opened: IntegrityFlagRow | null = openId ? rows.find((r) => r._id === openId) ?? null : null;
  const openedDays = opened ? daysRemaining(opened.appealDeadline) : null;
  const canAppeal = opened
    && opened.status === 'voided'
    && !opened.appealSubmittedAt
    && (openedDays === null || openedDays > 0);

  async function doAppeal() {
    if (!opened) return;
    const text = appealText.trim();
    if (text.length < 20) {
      toast.show({ tone: 'warning', message: 'Appeal must be at least 20 characters.' });
      return;
    }
    try {
      await appeal.mutateAsync({ id: opened._id, text });
      toast.show({ message: 'Appeal submitted. Your college will review it.' });
      setOpenId(null);
      setAppealText('');
    } catch (err) {
      toast.show({ tone: 'warning', message: err instanceof Error ? err.message : 'Could not submit appeal.' });
    }
  }

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">My profile · Integrity</p>
          <h1 className="h-page">Integrity flags</h1>
          <p className="subhead">
            Anything the invigilator system spotted on your test attempts. You have 7 days after a
            flag is voided to submit an appeal.
          </p>
        </div>
      </div>

      {summary && (
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-5">
          <StatCard label="Open" value={summary.open} tone="warning" />
          <StatCard label="Cleared" value={summary.cleared} tone="success" />
          <StatCard label="Explained" value={summary.explained} tone="success" />
          <StatCard label="Retest" value={summary.retest} tone="info" />
          <StatCard label="Voided" value={summary.voided} tone="danger" />
        </div>
      )}

      <Card padding="none">
        <AsyncBoundary
          kind="table"
          inCard
          loading={q.isLoading}
          error={q.error}
          empty={!q.isLoading && rows.length === 0}
          emptyTitle="No integrity flags"
          emptyHint="You're clear. Keep giving your tests calmly."
          onRetry={() => void q.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Kind</th>
                  <th className="hide-mobile">Assessment</th>
                  <th>Severity</th>
                  <th>Status</th>
                  <th>Appeal by</th>
                  <th className="r">When</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const days = daysRemaining(r.appealDeadline);
                  const isAppealable = r.status === 'voided' && !r.appealSubmittedAt && (days === null || days > 0);
                  const asmt = typeof r.assessment === 'object' ? r.assessment : null;
                  return (
                    <tr key={r._id} onClick={() => setOpenId(r._id)} className="cursor-pointer">
                      <td>
                        <div className="cell-title">{KIND_LABEL[r.kind] ?? r.kind}</div>
                        {r.reason && <div className="cell-sub truncate">{r.reason}</div>}
                      </td>
                      <td className="hide-mobile">{asmt?.name ?? '—'}</td>
                      <td><span className={`badge ${SEVERITY_TONE[r.severity] ?? ''}`}>{r.severity}</span></td>
                      <td><span className={`badge ${STATUS_TONE[r.status] ?? ''}`}>{r.status}</span></td>
                      <td>
                        {r.appealSubmittedAt
                          ? <span className="badge badge-info">Submitted</span>
                          : isAppealable
                            ? <span className={`badge ${days !== null && days <= 2 ? 'badge-danger' : 'badge-warning'}`}>
                                {days ?? '—'}d left
                              </span>
                            : <span className="cell-sub">—</span>}
                      </td>
                      <td className="r cell-sub">
                        {formatIndianDate(new Date(r.createdAt))} {formatTime(r.createdAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </Card>

      <Drawer
        open={Boolean(opened)}
        onClose={() => { setOpenId(null); setAppealText(''); }}
        title={opened ? (KIND_LABEL[opened.kind] ?? opened.kind) : ''}
        size="md"
      >
        {opened && (
          <div className="stack">
            <dl className="dl">
              <dt>Assessment</dt>
              <dd>{typeof opened.assessment === 'object' ? opened.assessment?.name ?? '—' : '—'}</dd>
              <dt>Severity</dt>
              <dd><span className={`badge ${SEVERITY_TONE[opened.severity] ?? ''}`}>{opened.severity}</span></dd>
              <dt>Status</dt>
              <dd><span className={`badge ${STATUS_TONE[opened.status] ?? ''}`}>{opened.status}</span></dd>
              <dt>Flagged</dt>
              <dd>{formatIndianDate(new Date(opened.createdAt))} {formatTime(opened.createdAt)}</dd>
              {opened.resolvedAt && (<>
                <dt>Resolved</dt>
                <dd>{formatIndianDate(new Date(opened.resolvedAt))}</dd>
              </>)}
              {opened.appealDeadline && (<>
                <dt>Appeal by</dt>
                <dd>{formatIndianDate(new Date(opened.appealDeadline))}</dd>
              </>)}
            </dl>

            {opened.reason && (
              <div className="notice">
                <b>Reviewer note</b>
                <div>{opened.reason}</div>
              </div>
            )}

            {opened.appealSubmittedAt ? (
              <Notice tone="info" title="Appeal submitted">
                Submitted {formatIndianDate(new Date(opened.appealSubmittedAt))}. Your college will
                review and reply. You'll get a notification.
                {opened.appealText && (
                  <div className="mt-2">
                    <b>Your appeal</b>
                    <div className="cell-sub">{opened.appealText}</div>
                  </div>
                )}
              </Notice>
            ) : canAppeal ? (
              <div>
                <label className="cell-sub" htmlFor="appeal-text">
                  Your appeal (20–2000 characters). Explain what actually happened.
                </label>
                <textarea
                  id="appeal-text"
                  className="input mt-1 w-full"
                  rows={6}
                  value={appealText}
                  onChange={(e) => setAppealText(e.target.value)}
                  maxLength={2000}
                />
                <div className="flex justify-end gap-2 mt-2">
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setOpenId(null); setAppealText(''); }}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => void doAppeal()}
                    disabled={appeal.isPending || appealText.trim().length < 20}
                  >
                    {appeal.isPending ? 'Submitting…' : 'Submit appeal'}
                  </button>
                </div>
              </div>
            ) : opened.status === 'voided' && openedDays !== null && openedDays <= 0 ? (
              <Notice tone="warning" title="Appeal window closed">
                The 7-day appeal window has ended. Contact your TPO if you still want to raise it.
              </Notice>
            ) : (
              <Notice tone="info" title="Nothing to appeal">
                This flag isn't in the appealable state. Only voided flags accept appeals within 7 days.
              </Notice>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: number; tone: 'success' | 'warning' | 'info' | 'danger' }) {
  const bg: Record<typeof tone, string> = { success: 'bg-success', warning: 'bg-warning', info: 'bg-info', danger: 'bg-danger' };
  return (
    <div className={`card card-pad ${bg[tone]}`}>
      <div className="cell-sub">{label}</div>
      <div className="h-section">{value}</div>
    </div>
  );
}
